import React, {useState, useEffect, useCallback, useRef} from 'react';
import styled from 'styled-components';
import TabPanel from 'components/Tabs/TabPanel';
import Paper from 'components/Paper';
import Icon from '@mdi/react';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import {debounce} from 'lodash';
import {mdiServerNetwork, mdiTable} from '@mdi/js';
import VLANLegend from 'features/SwitchConnectivity/VLANLegend/VLANLegend';
import SwitchSearch from 'features/SwitchConnectivity/Search/SwitchSearch';
import SwitchSearchCheckboxes, {
  SwitchSearchOptions,
} from 'features/SwitchConnectivity/Search/SwitchSearchCheckboxes';
import SwitchTableContainer from 'features/SwitchConnectivity/SwitchStack/SwitchTableContainer';
import SwitchDiagramContainer from 'features/SwitchConnectivity/SwitchStack/SwitchDiagramContainer';
import type SwitchConnectivityState from 'features/SwitchConnectivity/interfaces/SwitchConnectivityState';
import type Action from 'redux/Action';

//#region styled-components
const StyledGridItemAlignRight = styled(Grid)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const StyledVlanLegendGrid = styled(Grid)`
  padding: 0;
  &.pinned {
    position: fixed;
    top: 120px;
    margin: 0 auto;
    z-index: 1000;
  }
`;

const StyledTabs = styled(Tabs)`
  margin-top: 8px;
  .MuiTabs-indicator {
    height: 6px;
    background-color: ${props => props.theme.activeStateBlue};
  }
`;

const StyledTab = styled(Tab)`
  margin-bottom: 15px;
  min-width: 217px;
  padding: 0px 16px;
  transition: 200ms;
  margin-right: 1.5rem;
  &.MuiTab-textColorInherit {
    color: ${props => props.theme.text};
    border-radius: 10px;
    border: 4px solid ${props => props.theme.grey};
  }
  &.MuiTab-textColorInherit.Mui-selected {
    background-color: ${props => props.theme.activeStateBlue};
    color: ${props => props.theme.black};
    border: 4px solid ${props => props.theme.activeStateBlue};
  }
  &.MuiTab-labelIcon {
    min-height: 56px;
  }
  .MuiTab-wrapper {
    flex-direction: row;
    column-gap: 0.5rem;
  }
  .MuiTab-wrapper > *:first-child {
    margin-bottom: 4px;
  }
`;

const StyledSearchBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
//#endregion styled-components

export default function SwitchStacksContainer({
  idfName,
  switchStackName,
  state,
  dispatch,
  refresh,
}: {
  idfName?: string;
  switchStackName: string;
  state: SwitchConnectivityState;
  dispatch: React.Dispatch<Action>;
  refresh: () => void;
}) {
  const diagramAnchorIDPrefix: string = 'switch-diagram-';
  const tableAnchorIDPrefix: string = 'switch-table-';

  const {switchStacks, filteredFlattenedSwitchData, portHistoryState} = state;

  const [value, setValue] = useState(0);
  const [switchSearchValue, setSwitchSearchValue] = useState<string>('');
  const [switchSearchOptions, setSwitchSearchOptions] = useState<SwitchSearchOptions>({
    includeCurrentData: true,
    includeHistoricalData: false,
  });

  // #region functions
  const handleChange = (event: any, newValue: any) => {
    setValue(newValue);
  };

  function handleLegendItemSelectionChanged(
    switchStackName: string,
    vlanNumber: any,
    isSelected: any
  ) {
    dispatch({
      type: 'LEGEND_ITEM_SELECTION_CHANGED',
      payload: {switchStackName, vlanNumber, isSelected},
    });
  }

  function handleClearLegendItems() {
    setSwitchSearchValue('');
    dispatch({
      type: 'LEGEND_ITEM_CLEAR',
      payload: switchStackName,
    });
  }

  const handleSearchChanged = (value: string, options: SwitchSearchOptions) => {
    debouncedHandleSearchTextChanged(value, options);
  };

  const dispatchSearchTextChanged = (searchText: any, searchOptions: SwitchSearchOptions) =>
    dispatch({
      type: 'SEARCH_TEXT_CHANGED',
      payload: {switchStackName, searchText, searchOptions},
    });

  const debouncedHandleSearchTextChanged = useCallback(
    debounce(dispatchSearchTextChanged, 500),
    []
  );
  // #endregion functions

  useEffect(() => {
    handleSearchChanged(switchSearchValue, switchSearchOptions);
  }, [switchSearchValue, switchSearchOptions]);

  return (
    <Grid item xs={12}>
      <Paper>
        <Grid container spacing={2}>
          <StyledVlanLegendGrid xs={12} item>
            <VLANLegend
              switchStackName={switchStackName}
              switchState={state}
              idfName={idfName}
              handleClearLegendItems={handleClearLegendItems}
              onLegendItemSelectionChanged={handleLegendItemSelectionChanged}
              refresh={refresh}
            />
          </StyledVlanLegendGrid>

          <Grid item xs={6}>
            <StyledTabs value={value} onChange={handleChange}>
              <StyledTab icon={<Icon path={mdiServerNetwork} size={1} />} label="DIAGRAM" />
              <StyledTab icon={<Icon path={mdiTable} size={1} />} label="TABLE" />
            </StyledTabs>
          </Grid>
          <StyledGridItemAlignRight item xs={6}>
            <StyledSearchBoxContainer>
              <SwitchSearch value={switchSearchValue} setSearchValue={setSwitchSearchValue} />
              <SwitchSearchCheckboxes
                options={switchSearchOptions}
                setOptions={setSwitchSearchOptions}
              />
            </StyledSearchBoxContainer>
          </StyledGridItemAlignRight>

          <Grid item xs={12}>
            <TabPanel value={value} index={0}>
              <SwitchDiagramContainer
                switchStackName={switchStackName}
                switchStacks={switchStacks}
                anchorIDPrefix={diagramAnchorIDPrefix}
                portHistoryData={portHistoryState}
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <SwitchTableContainer
                switchStackName={switchStackName}
                anchorIDPrefix={tableAnchorIDPrefix}
                filteredFlattenedSwitchData={filteredFlattenedSwitchData}
                portHistoryData={portHistoryState}
              />
            </TabPanel>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}
